import React, { useEffect, useState } from 'react';
import { Button, DatePicker, Input, Select, Table, message } from 'antd';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import AppShell from './components/AppShell';
import Panel from './components/Panel';
import TableSkeleton from './components/TableSkeleton';
import Field from './components/Field';
import { Badge } from './components/StatusPill';
import axiosInstance from './utils/axiosInstance';
import { resetOrderTimer } from './utils/orderTimer';
import { money } from './utils/format';

const { Column } = Table;
const { RangePicker } = DatePicker;

interface OrderType {
    key: React.Key;
    id: number;
    User: {
        id: number;
        username: string;
        phoneNo: string;
    },
    Product: {
        name: string;
        seller: string;
    },
    comission: number;
    orderStatus: string;
    referenceNo: string;
    totalAmount: number;
}

/** Every server-side filter the Orders query understands. */
interface OrderFilters {
    orderId?: string;
    userId?: string;
    orderNo?: string;
    username?: string;
    phoneNo?: string;
    product?: string;
    seller?: string;
    status?: string;
    fromDate?: string;
    toDate?: string;
    minAmount?: string;
    maxAmount?: string;
}

const OrderPage: React.FC = () => {
    const [orders, setOrders] = useState<OrderType[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);

    const [filters, setFilters] = useState<OrderFilters>({});
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);

    // Reads straight from the passed args so callers never fight React's async state.
    const fetchOrders = async (f: OrderFilters = filters, targetPage = 1, targetSize = pageSize) => {
        setLoading(true);
        setPage(targetPage);
        setPageSize(targetSize);
        try {
            const response = await axiosInstance.post("admin/protected/getOrders", {
                ...f,
                page: targetPage,
                pageSize: targetSize,
            });
            const data = response?.data?.data;
            setTotal(response?.data?.total ?? 0);
            if (data) {
                setOrders(data.map((e: any) => ({
                    id: e.id,
                    key: e.id,
                    comission: e.comission,
                    orderStatus: e.orderStatus,
                    Product: {
                        name: e.Product?.name,
                        seller: e.Product?.seller,
                    },
                    referenceNo: e.referenceNo,
                    totalAmount: e.totalAmount,
                    User: {
                        id: e.User?.id,
                        phoneNo: e.User?.phoneNo,
                        username: e.User?.username,
                    },
                } as OrderType)));
            } else {
                setOrders([]);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders({}, 1, 20);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setField = (key: keyof OrderFilters) => (
        e: React.ChangeEvent<HTMLInputElement>,
    ) => setFilters((prev) => ({ ...prev, [key]: e.target.value }));

    const search = () => fetchOrders(filters, 1, pageSize);

    const reset = () => {
        setFilters({});
        fetchOrders({}, 1, pageSize);
    };

    return (
        <AppShell title="订单" subtitle={`${total} 订单`}>
            <Panel className="mb-4">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                    <Field label="订单ID">
                        <Input allowClear value={filters.orderId} placeholder="订单ID" onChange={setField('orderId')} onPressEnter={search} />
                    </Field>
                    <Field label="用户ID">
                        <Input allowClear value={filters.userId} placeholder="用户ID" onChange={setField('userId')} onPressEnter={search} />
                    </Field>
                    <Field label="订单号">
                        <Input allowClear value={filters.orderNo} placeholder="订单号" onChange={setField('orderNo')} onPressEnter={search} />
                    </Field>
                    <Field label="状态">
                        <Select
                            allowClear
                            value={filters.status}
                            placeholder="全部状态"
                            onChange={(v) => setFilters((prev) => ({ ...prev, status: v }))}
                            options={[
                                { value: 'pending', label: '待处理' },
                                { value: 'completed', label: '已完成' },
                            ]}
                        />
                    </Field>
                    <Field label="用户名">
                        <Input allowClear value={filters.username} placeholder="用户名" onChange={setField('username')} onPressEnter={search} />
                    </Field>
                    <Field label="手机号">
                        <Input allowClear value={filters.phoneNo} placeholder="手机号" onChange={setField('phoneNo')} onPressEnter={search} />
                    </Field>
                    <Field label="商品">
                        <Input allowClear value={filters.product} placeholder="商品名称" onChange={setField('product')} onPressEnter={search} />
                    </Field>
                    <Field label="卖家">
                        <Input allowClear value={filters.seller} placeholder="卖家" onChange={setField('seller')} onPressEnter={search} />
                    </Field>
                    <Field label="日期范围" className="col-span-2">
                        <RangePicker
                            showTime
                            className="w-full"
                            onChange={(dates) => setFilters((prev) => ({
                                ...prev,
                                fromDate: dates?.[0]?.toISOString(),
                                toDate: dates?.[1]?.toISOString(),
                            }))}
                        />
                    </Field>
                    <Field label="最小金额">
                        <Input allowClear type="number" value={filters.minAmount} placeholder="最小金额" onChange={setField('minAmount')} onPressEnter={search} />
                    </Field>
                    <Field label="最大金额">
                        <Input allowClear type="number" value={filters.maxAmount} placeholder="最大金额" onChange={setField('maxAmount')} onPressEnter={search} />
                    </Field>
                </div>

                <div className="mt-4 flex gap-2">
                    <Button type="primary" icon={<SearchOutlined />} onClick={search}>搜索</Button>
                    <Button icon={<ReloadOutlined />} onClick={reset}>重置</Button>
                </div>
            </Panel>

            <Panel flush>
            {loading ? <TableSkeleton rows={5} columns={7} /> : (
            <Table<OrderType>
                dataSource={orders}
                scroll={{ x: 'max-content' }}
                size="middle"
                pagination={{
                    current: page,
                    pageSize,
                    total,
                    showSizeChanger: true,
                    pageSizeOptions: [20, 50, 100, 200],
                    showTotal: (t, r) => `${r[0]}-${r[1]} / 共 ${t} 条`,
                    onChange: (p, ps) => fetchOrders(filters, p, ps),
                }}
            >
                <Column title="ID" dataIndex="id" key="id" width={70} />
                <Column
                    title="用户名"
                    key="username"
                    render={(_: any, record: OrderType) => (
                        <span className="font-semibold text-ink">{record.User?.username}</span>
                    )}
                />
                <Column
                    title="电话"
                    key="phoneNo"
                    render={(_: any, record: OrderType) => (
                        <span className="tabular text-muted">{record.User?.phoneNo}</span>
                    )}
                />
                <Column
                    title="商品"
                    key="productName"
                    render={(_: any, record: OrderType) => (
                        <>{record.Product?.name}</>
                    )}
                />
                <Column title="总数 (RM)" key="totalAmount" className='tabular'
                    render={(_: any, record: OrderType) => (
                        <span className="font-semibold text-ink">{money(record.totalAmount)}</span>
                    )}
                />
                <Column title="收入 (RM)" key="comission" className='tabular'
                    render={(_: any, record: OrderType) => (
                        <span className="font-semibold text-emerald-600">+{money(record.comission)}</span>
                    )}
                />
                <Column title="订单号" key="referenceNo"
                    render={(_: any, record: OrderType) => (
                        <span className="tabular tracking-wide text-muted">{record.referenceNo}</span>
                    )}
                />
                <Column
                    title="状态"
                    key="orderStatus"
                    render={(_: any, record: OrderType) => {
                        const s = record?.orderStatus;
                        return s === 'completed' ? <Badge tone="positive" dot>已完成</Badge>
                            : s === 'pending' ? <Badge tone="caution" dot>待处理</Badge>
                                : <Badge dot>{s?.toUpperCase()}</Badge>;
                    }}
                />
                <Column
                    title="操作"
                    key="action"
                    render={(_: any, record: OrderType) => {
                        return record.orderStatus === 'pending' ? (
                            <Button size="small" type="dashed" onClick={async () => {
                                try {
                                    await resetOrderTimer({ orderId: record.id });
                                    message.success('定时已重置');
                                    search();
                                } catch (e: any) {
                                    message.error(e?.response?.data?.message || e?.message || '重置定时失败');
                                }
                            }}>重置倒计时</Button>
                        ) : null;
                    }}
                />
            </Table>
            )}
            </Panel>
        </AppShell>
    );
}

export default OrderPage;
